import { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { mcGetTokenPools, mcGetPoolReserves } from '../lib/api';
import { Card, CardContent } from './ui/card';
import { Tabs, TabsList, TabsTrigger, TabsContent } from './ui/tabs';
import { Badge } from './ui/badge';
import { Skeleton } from './ui/skeleton';
import { AlertCircle, ArrowLeftRight, Droplets, ExternalLink } from 'lucide-react';
import { fmtUsd, fmtPrice } from '../lib/numfmt';
import PoolList from './PoolList';
import PoolSwapSimulator from './PoolSwapSimulator';
import LiquidityImpactSimulator from './LiquidityImpactSimulator';

/**
 * Unified, DexScreener-style simulator for ANY chain+token (Krown included):
 * discover live pools → pick a pool → simulate swaps & liquidity changes.
 */
export default function TokenPoolSimulator({ chain, token }) {
  const [meta, setMeta] = useState(null);
  const [pools, setPools] = useState([]);
  const [loadingPools, setLoadingPools] = useState(true);
  const [poolsError, setPoolsError] = useState(null);

  const [selected, setSelected] = useState(null);
  const [reserves, setReserves] = useState(null);
  const [loadingRes, setLoadingRes] = useState(false);
  const [resError, setResError] = useState(null);

  const loadPools = useCallback(async () => {
    setLoadingPools(true); setPoolsError(null); setSelected(null); setReserves(null);
    try {
      const d = await mcGetTokenPools(chain, token);
      setMeta(d);
      const list = d.pools || [];
      setPools(list);
      if (list.length === 0) setPoolsError('No live pools with liquidity found for this token.');
      else setSelected(list[0]);
    } catch (e) {
      setPoolsError(e?.response?.data?.detail || 'Failed to load pools for this token.');
      setMeta(null); setPools([]);
    } finally {
      setLoadingPools(false);
    }
  }, [chain, token]);

  useEffect(() => { loadPools(); }, [loadPools]);

  useEffect(() => {
    if (!selected?.pair_address) { setReserves(null); return; }
    let alive = true;
    setLoadingRes(true); setResError(null); setReserves(null);
    mcGetPoolReserves(chain, selected.pair_address)
      .then((r) => { if (alive) setReserves(r); })
      .catch((e) => { if (alive) setResError(e?.response?.data?.detail || 'Could not read pool reserves.'); })
      .finally(() => { if (alive) setLoadingRes(false); });
    return () => { alive = false; };
  }, [chain, selected]);

  if (loadingPools) {
    return <Skeleton className="h-96 w-full rounded" data-testid="token-pool-sim-loading" />;
  }
  if (poolsError && pools.length === 0) {
    return (
      <Card data-testid="token-pool-sim-error">
        <CardContent className="pt-6">
          <div className="flex items-center gap-2 text-sm text-warning">
            <AlertCircle className="w-4 h-4" /> {poolsError}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4" data-testid="token-pool-simulator">
      {/* Token context header */}
      {meta && (
        <div className="flex items-center gap-3 flex-wrap border border-border p-3" data-testid="sim-token-context">
          {meta.image && <img src={meta.image} alt="" className="w-8 h-8 rounded-full" />}
          <div>
            <div className="font-bold flex items-center gap-2">
              {meta.symbol || 'Token'}
              <Badge variant="outline" className="text-[10px]">{chain}</Badge>
            </div>
            <div className="text-xs text-muted-foreground font-mono">{meta.name || ''}</div>
          </div>
          <div className="ml-auto flex items-center gap-4 text-right">
            <div>
              <div className="mono-label text-[10px]">Price</div>
              <div className="font-mono font-bold text-sm">{fmtPrice(meta.price_usd)}</div>
            </div>
            <div>
              <div className="mono-label text-[10px]">Market cap</div>
              <div className="font-mono font-bold text-sm">{fmtUsd(meta.market_cap || meta.fdv)}</div>
            </div>
            {chain !== 'krown' && meta.address && (
              <Link to={`/explorer/${chain}/token/${meta.address}`} className="text-xs text-primary hover:underline inline-flex items-center gap-1" data-testid="sim-view-token">
                Token <ExternalLink className="w-3 h-3" />
              </Link>
            )}
          </div>
        </div>
      )}

      {/* Pool list */}
      <div>
        <div className="mono-label mb-2">Select a pool ({pools.length})</div>
        <PoolList pools={pools} selected={selected?.pair_address} onSelect={setSelected} />
      </div>

      {/* Simulators */}
      <Card>
        <CardContent className="pt-5">
          <Tabs defaultValue="swap" className="w-full">
            <TabsList className="grid grid-cols-2 w-full max-w-xs mb-4" data-testid="sim-tabs">
              <TabsTrigger value="swap" data-testid="sim-tab-swap"><ArrowLeftRight className="w-3.5 h-3.5 mr-1.5" /> Swap</TabsTrigger>
              <TabsTrigger value="liquidity" data-testid="sim-tab-liquidity"><Droplets className="w-3.5 h-3.5 mr-1.5" /> Liquidity</TabsTrigger>
            </TabsList>
            <TabsContent value="swap">
              <PoolSwapSimulator tokenMeta={meta} pool={selected} reserves={reserves} loading={loadingRes} error={resError} />
            </TabsContent>
            <TabsContent value="liquidity">
              <LiquidityImpactSimulator tokenMeta={meta} pool={selected} reserves={reserves} loading={loadingRes} error={resError} />
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}
